import React, {Component} from 'react';
import Resize from 'react-resize-detector';
import PropTypes from 'prop-types';
import _ from 'lodash';

import HoverOver from '../HoverOver';
import StateOutline from "../MapElements/StateOutline";
import StateCountyHovers from "../MapElements/StateCountyHovers";
import StateCountyBorders from "../MapElements/StateCountyBorders";
import StateCountyShapes from "../MapElements/StateCountyShapes";
import StateCityLabels from "../MapElements/StateCityLabels";

import {TEXT} from "../../../utilities/strings";

import '../MapElements/Map.css';

/**
 * Component for rendering the full state map at the county level with
 * all borders, labels, and colored counties for the provided stateFips.
 *
 * @property colorField - field in data objects that holds the quartile value for classification
 * @property legendField - field in data objects that holds the numeric value for classification
 * @property data - array of location models to calculate the map colors and values
 * @property dict - dictionary of data keyed by the state fips
 * @property stateFips - selected state fips numeric id value
 * @property selectedCost - selected cost string value
 * @property selectedIds - array of county fips ids to style as selected
 * @property onClick - callback when a state is clicked (stateFips,cent)
 * @property onAltClick - callback when a state is alt clicked (stateFips,cent)
 *
 * @example
 * return (
 *   <StateMap
 *     colorField={mapProperty}
 *     legendField={legendProperty}
 *     stateFips={this.props.selectedStateFips}
 *     selectedIds={[this.props.selectedCountyFips, this.props.comparisonCountyFips]}
 *     selectedCost={this.props.selectedCost}
 *     data={this.props.countiesData}
 *     dict={_.keyBy(this.props.countiesData, VARIABLES.CountyID)}
 *     onClick={this.props.onSelectedCountyChange}
 *     onAltClick={this.props.onComparisonCountyChange}
 *   />
 * )
 */
export default class StateMap extends Component {

  static propTypes = {
    colorField: PropTypes.string,
    legendField: PropTypes.string,
    stateFips: PropTypes.number,
    selectedCost: PropTypes.string,
    selectedIds: PropTypes.array,
    data: PropTypes.array,
    dict: PropTypes.object,
    onClick: PropTypes.func,
    onAltClick: PropTypes.func
  };

  wrapper = React.createRef()

  state = {
    width: 500,
    height: 500
  };

  /**
   * Size map initially
   */
  componentDidMount() {
    this.handleResize(this.wrapper.current.offsetWidth)
  }

  /**
   * Check if window has resized, and size map accordingly
   */
  handleResize = (resizedWidth) => {
    const maxHeight = window.innerWidth > 1600 ? 700 : 500;
    const width = Math.max(360, resizedWidth)
    const height = Math.min(width, maxHeight)

    this.setState({width, height});
  }

  /**
   * get data for mouse hover over for currently hovered location
   * and set hover state value for display
   */
  handleHoverOver = (countyFips, coords) => {
    const hover = {
      data: _.get(this.props, ['dict', countyFips], null),
      props: this.props,
      coords,
    };

    this.setState({hover});
  }

  /**
   * clear hover display
   */
  handleHoverOut = () => {
    this.setState({hover: null});
  }

  render() {
    return (
      <div ref={this.wrapper} className="StateMap SvgMap">
        <Resize handleWidth onResize={this.handleResize}/>
        <svg id="state-map" role="img"
             aria-labelledby="state-map-desc"
             width={this.state.width}
             height={this.state.height}>
          <desc id="state-map-desc">
            {TEXT.accessible_map_description}
          </desc>
          <StateOutline
            stateFips={this.props.stateFips}
            width={this.state.width}
            height={this.state.height}
          />
          <StateCountyShapes
            stateFips={this.props.stateFips}
            data={this.props.data}
            dict={this.props.dict}
            width={this.state.width}
            height={this.state.height}
            dimension={this.props.colorField}
          />
          <StateCountyBorders
            stateFips={this.props.stateFips}
            width={this.state.width}
            height={this.state.height}
          />
          <StateCountyHovers
            width={this.state.width}
            height={this.state.height}
            stateFips={this.props.stateFips}
            selectedIds={this.props.selectedIds}
            onClick={this.props.onClick}
            onAltClick={this.props.onAltClick}
            onHoverOver={this.handleHoverOver}
            onHoverOut={this.handleHoverOut}
          />
          <StateCityLabels
            stateFips={this.props.stateFips}
            width={this.state.width}
            height={this.state.height}
          />
        </svg>
        <HoverOver data={this.state.hover}/>
      </div>
    );
  }
}
